import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { queryCache } from '../lib/queryCache';
import { getStudyGroups } from '../services/api';

const Header = ({ toggleSidebar, title, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // ✅ State for mobile search visibility
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // --- Track Pending Requests & Unread Messages for Mobile Burger Menu ---
  const [hasNotification, setHasNotification] = useState(() => {
    const cached = queryCache.get("study_groups_mapped");
    if (Array.isArray(cached)) {
      return cached.some(
        (g) =>
          (!g.isShared && Number(g.pendingRequestsCount || 0) > 0) ||
          Number(g.unreadMessagesCount || 0) > 0
      );
    }
    return false;
  });

  useEffect(() => {
    const checkStatus = () => {
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached)) {
        setHasNotification(
          cached.some(
            (g) =>
              (!g.isShared && Number(g.pendingRequestsCount || 0) > 0) ||
              Number(g.unreadMessagesCount || 0) > 0
          )
        );
      } else {
        getStudyGroups()
          .then((groups) => {
            if (Array.isArray(groups)) {
              setHasNotification(
                groups.some(
                  (g) =>
                    (!g.isShared && Number(g.pendingRequestsCount || 0) > 0) ||
                    Number(g.unreadMessagesCount || 0) > 0
                )
              );
            }
          })
          .catch(() => {});
      }
    };

    if (!Array.isArray(queryCache.get("study_groups_mapped"))) {
      checkStatus();
    }

    const handleRequestsChanged = (e) => {
      if (e?.detail && typeof e.detail.totalPendingRequests === "number") {
        if (e.detail.totalPendingRequests > 0) {
          setHasNotification(true);
          return;
        }
      }
      checkStatus();
    };

    const handleMessageReceived = () => {
      setHasNotification(true);
    };

    const handleChatRead = (e) => {
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached) && e?.detail?.groupId) {
        const updated = cached.map((g) =>
          String(g.id) === String(e.detail.groupId)
            ? { ...g, unreadMessagesCount: 0, hasUnreadMessages: false }
            : g
        );
        setHasNotification(
          updated.some(
            (g) =>
              (!g.isShared && Number(g.pendingRequestsCount || 0) > 0) ||
              Number(g.unreadMessagesCount || 0) > 0
          )
        );
        return;
      }
      checkStatus();
    };

    window.addEventListener("study_group_requests_changed", handleRequestsChanged);
    window.addEventListener("study_group_updated", checkStatus);
    window.addEventListener("study_group_message_received", handleMessageReceived);
    window.addEventListener("study_group_chat_read", handleChatRead);

    return () => {
      window.removeEventListener("study_group_requests_changed", handleRequestsChanged);
      window.removeEventListener("study_group_updated", checkStatus);
      window.removeEventListener("study_group_message_received", handleMessageReceived);
      window.removeEventListener("study_group_chat_read", handleChatRead);
    };
  }, []);

  // ✅ Supports dynamic routes like /library/:id
  const getPageTitle = (path) => {
    if (path === '/library' || path.startsWith('/library/')) return 'My Library';
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/board-prep') return 'Board Prep';
    if (path === '/study-group' || path === '/board-prep/view') return 'Study Group';
    if (path.startsWith('/study-group/') && path.endsWith('/hub')) return 'Group Hub';
    if (path === '/analytics') return 'Analytics';
    if (path === '/achievements') return 'Achievement Zone';
    if (path === '/profile') return 'My Profile';
    if (path === '/create-reviewer') return 'Create Reviewer';
    return 'ReviewMate';
  };

  // ✅ If a page passes title it can override, otherwise use route title
  const finalTitle = title || getPageTitle(location.pathname);

  const date = new Date();
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  // ✅ Hide search on distraction-free zones
  const hideSearchPrefixes = ['/dashboard', '/analytics', '/profile', '/achievements', '/create-reviewer'];

  const showSearch =
    !hideSearchPrefixes.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/')
    ) && !location.pathname.startsWith('/study-group/');

  const isCreatePage = location.pathname === '/create-reviewer';

  // ✅ Search should update URL on Library and Study Group pages
  const isSearchablePage = useMemo(() => {
    return (
      location.pathname === '/library' ||
      location.pathname.startsWith('/library/') ||
      location.pathname === '/study-group'
    );
  }, [location.pathname]);

  // ✅ Controlled input synced with URL ?q=
  const [search, setSearch] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearch(params.get('q') || '');
  }, [location.search]);

  // ✅ Close mobile search when route changes (optional UX improvement)
  useEffect(() => {
    setIsMobileSearchOpen(false);
  }, [location.pathname]);

  const updateQuery = (value) => {
    const params = new URLSearchParams(location.search);
    const trimmed = value.trim();

    if (trimmed) params.set('q', trimmed);
    else params.delete('q');

    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (isSearchablePage) updateQuery(val);
  };

  const handleKeyDown = (e) => {
    if (!isSearchablePage) return;
    if (e.key === 'Enter') {
      updateQuery(search);
      // Optional: Close mobile search on enter
      // setIsMobileSearchOpen(false); 
    }
    if (e.key === 'Escape') {
      setSearch('');
      updateQuery('');
      setIsMobileSearchOpen(false);
    }
  };

  return (
    <header className="h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-4 md:px-8 sticky top-0 z-10 transition-all duration-300 relative">
      
      {/* 📱 MOBILE SEARCH OVERLAY */}
      {isMobileSearchOpen && (
        <div className="absolute inset-0 bg-white z-20 flex items-center px-4 md:hidden animate-fade-in border-b border-gray-200">
          <i className="fa-solid fa-magnifying-glass text-blue-500 mr-3"></i>
          <input
            type="text"
            autoFocus
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder={location.pathname === '/study-group' ? 'Search study groups...' : 'Search library...'}
            className="flex-1 bg-transparent border-none outline-none text-gray-800 text-sm h-full placeholder-gray-400"
          />
          {search && (
            <button
              onClick={() => {
                setSearch('');
                updateQuery('');
              }}
              className="mr-3 text-gray-400 hover:text-gray-600"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
          <button
            onClick={() => setIsMobileSearchOpen(false)}
            className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 hover:text-gray-700 transition"
          >
            Cancel
          </button>
        </div>
      )}

      {/* LEFT CONTENT (Hidden when mobile search is open to prevent clutter, or kept if overlay is used) */}
      <div className={`flex items-center gap-4 ${isMobileSearchOpen ? 'opacity-0 md:opacity-100' : 'opacity-100'}`}>
        {isCreatePage ? (
          <button
            onClick={onBack ? onBack : () => navigate(-1)}
            className="md:hidden text-gray-500 hover:text-blue-600 transition-colors"
          >
            <i className="fa-solid fa-arrow-left text-xl"></i>
          </button>
        ) : (
          <button
            onClick={toggleSidebar}
            className="md:hidden text-gray-600 hover:text-gray-900 transition-colors relative p-1"
            title="Toggle Menu"
          >
            <i className="fa-solid fa-bars text-xl"></i>
            {hasNotification && (
              <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"></span>
            )}
          </button>
        )}

        <div>
          <h2 className="text-lg font-bold text-gray-800 tracking-tight leading-none flex items-center gap-2">
            {finalTitle}
            {isCreatePage && (
              <span className="hidden md:inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-wider rounded-full border border-blue-100">
                Studio
              </span>
            )}
          </h2>

          <p className="text-[10px] text-gray-400 font-medium mt-1 hidden md:block">
            {formattedDate}
          </p>
        </div>
      </div>

      {/* RIGHT CONTENT */}
      <div className={`flex items-center gap-2 md:gap-5 ${isMobileSearchOpen ? 'opacity-0 md:opacity-100' : 'opacity-100'}`}>
        
        {showSearch && (
          <>
            {/* 🖥️ Desktop Search Input */}
            <div className="relative hidden md:block group">
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  location.pathname === '/study-group'
                    ? 'Search study groups...'
                    : isSearchablePage
                    ? 'Search library...'
                    : 'Search resources...'
                }
                className="pl-10 pr-10 py-2 rounded-xl border border-gray-200 text-sm w-64 bg-gray-50/50
                           focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10
                           outline-none transition-all duration-300 placeholder-gray-400 text-gray-700"
              />

              <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-2.5 text-gray-400 group-hover:text-blue-500 transition-colors"></i>

              {search && isSearchablePage && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    updateQuery('');
                  }}
                  className="absolute right-2 top-2 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                  aria-label="Clear search"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>

            {/* 📱 Mobile Search Trigger Button */}
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition"
            >
              <i className="fa-solid fa-magnifying-glass text-lg"></i>
            </button>
          </>
        )}

      </div>
    </header>
  );
};

export default Header;
